/**
 * i18next configuration
 * 
 * Sets up internationalization for the application with support for:
 * - Multiple languages (EN, JA, ZH-Hans, KO, ES)
 * - Language detection (browser + manual)
 * - Language persistence (localStorage opt-in)
 * - Namespace support for different sections
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enTranslations from '../../../locales/en/translation.json';
import jaTranslations from '../../../locales/ja/translation.json';
import zhHansTranslations from '../../../locales/zh-Hans/translation.json';
import koTranslations from '../../../locales/ko/translation.json';
import esTranslations from '../../../locales/es/translation.json';

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'ja', 'zh-Hans', 'ko', 'es'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Language names (localized to each language)
export const LANGUAGE_NAMES: Record<SupportedLanguage, Record<SupportedLanguage, string>> = {
  en: {
    en: 'English',
    ja: '日本語',
    'zh-Hans': '简体中文',
    ko: '한국어',
    es: 'Español',
  },
  ja: {
    en: '英語',
    ja: '日本語',
    'zh-Hans': '簡体字中国語',
    ko: '韓国語',
    es: 'スペイン語',
  },
  'zh-Hans': {
    en: 'English',
    ja: '日语',
    'zh-Hans': '简体中文',
    ko: '韩语',
    es: '西班牙语',
  },
  ko: {
    en: '영어',
    ja: '일본어',
    'zh-Hans': '간체 중국어',
    ko: '한국어',
    es: '스페인어',
  },
  es: {
    en: 'Inglés',
    ja: 'inglés',
    'zh-Hans': 'inglés',
    ko: 'inglés',
    es: 'Español',
  },
};

// Self-referential language names (always shown in their own language)
export const LANGUAGE_NATIVE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  ja: '日本語',
  'zh-Hans': '简体中文',
  ko: '한국어',
  es: 'Español',
};

// Locale mapping for date-fns
export const LOCALE_MAP: Record<SupportedLanguage, string> = {
  en: 'en-US',
  ja: 'ja-JP',
  'zh-Hans': 'zh-CN',
  ko: 'ko-KR',
  es: 'es-ES',
};

// Translation resources
const resources = {
  en: { translation: enTranslations },
  ja: { translation: jaTranslations },
  'zh-Hans': { translation: zhHansTranslations },
  ko: { translation: koTranslations },
  es: { translation: esTranslations },
};

// Get locale for date-fns formatting
export function getDateLocale(language: SupportedLanguage): string {
  return LOCALE_MAP[language];
}

// Check if language is supported
export function isLanguageSupported(language: string): language is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
}

// Get browser language
export function getBrowserLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const browserLang = navigator.language;
  const langCode = browserLang.split('-')[0];

  // Try full locale first
  if (isLanguageSupported(browserLang as SupportedLanguage)) {
    return browserLang as SupportedLanguage;
  }

  // Try language code only
  if (isLanguageSupported(langCode as SupportedLanguage)) {
    return langCode as SupportedLanguage;
  }

  // Map zh-CN to zh-Hans
  if (langCode === 'zh') {
    return 'zh-Hans';
  }

  return DEFAULT_LANGUAGE;
}

// Get saved language from localStorage (opt-in)
export function getSavedLanguage(): SupportedLanguage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const saved = localStorage.getItem('preferredLanguage');
  if (saved && isLanguageSupported(saved)) {
    return saved as SupportedLanguage;
  }

  return null;
}

// Save language to localStorage
export function saveLanguage(language: SupportedLanguage): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('preferredLanguage', language);
  }
}

// Initialize i18next
export async function initI18n(language?: SupportedLanguage): Promise<typeof i18n> {
  const resolvedLanguage =
    language ||
    getSavedLanguage() ||
    getBrowserLanguage() ||
    DEFAULT_LANGUAGE;

  if (!i18n.isInitialized) {
    i18n.use(initReactI18next);
  }

  await i18n.init({
    resources,
    lng: resolvedLanguage,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

  return i18n;
}

// Change language
export async function changeLanguage(language: SupportedLanguage): Promise<void> {
  saveLanguage(language);
  await i18n.changeLanguage(language);
}

// Get current language
export function getCurrentLanguage(): string {
  return i18n.language || DEFAULT_LANGUAGE;
}
